import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { motion } from 'framer-motion'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { breakpoints } from '@/config/layout'
import { Span } from '../../typography/Body'
import { StAppHeaderAction } from './AppHeader.styled'

import type { AppHeaderAction as AppHeaderActionType } from './AppHeader.types'

type Props = {
  action?: AppHeaderActionType
  reverse?: boolean
  color?: string
  style?: React.CSSProperties
}

const AnimatedAppHeaderAction = motion(StAppHeaderAction)

export const AppHeaderAction = ({ action, reverse, color, style }: Props) => {
  const isNative = Capacitor.isNativePlatform()
  // Fake this effect for dev purposes in the browser
  const isSmall = useMediaQuery(breakpoints.toM)
  const isMobile = isNative || isSmall
  const theme = useTheme()

  if (!action) {
    // Render a dummy to preserve Flex alignment
    return <StAppHeaderAction />
  }

  const { label, onClick, icon, iconSize } = action
  const iconStyle = isMobile
    ? {
        padding: `${theme.UI.SpacingPx.Space4}`,
        margin: `-${theme.UI.SpacingPx.Space4}`,
      }
    : { padding: '0px', margin: '0px' }

  return (
    <AnimatedAppHeaderAction
      key={label}
      initial={{
        opacity: 0,
        x: reverse ? 30 : -30,
      }}
      animate={{
        opacity: 1,
        x: 0,
      }}
      reverse={reverse}
      exit={{ opacity: 0, x: reverse ? 30 : -30 }}
      transition={{ type: 'spring', bounce: 0.1, duration: 0.4 }}
      onClick={onClick}
      // The label is hidden on a phone, where the button shows its icon only; it stays the name.
      aria-label={label}
      style={style}
    >
      {icon && !reverse ? (
        <FontAwesomeIcon size={iconSize} icon={icon} fontSize={20} color={color} style={iconStyle} />
      ) : null}
      {label && !isMobile ? (
        <Span type='BodyMediumBold' style={{ color }}>
          {label}
        </Span>
      ) : null}
      {icon && reverse ? (
        <FontAwesomeIcon size={iconSize} icon={icon} fontSize={20} color={color} style={iconStyle} />
      ) : null}
    </AnimatedAppHeaderAction>
  )
}
